import {
  IEmployeeWizardFormModel,
  IEmployeeStudioFormModel,
} from "../models/employeeWizardFormModel";
import { useEmployeeWizardForm } from "./useEmployeeWizardForm";
import { useLockOthersEventsToggle } from "./useLockOthersEventsToggle";
import { SubmitHandler } from "react-hook-form";
import { PageMode } from "../../../../../common/types";
import { useAppContext } from "../../../../../AppContext";
import { useNavigate, useParams } from "react-router-dom";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { routePaths } from "../../../../../common/infrastructure/routes/routePaths";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { normalizePhone } from "../../../../../common/components/forms/inputs/phoneField/utils/PhoneInputUtility";
import {
  getEmployeeRoles,
  outranks,
} from "../../../../../common/utilities/roleUtility";
import { useAuthentication } from "../../../../../common/infrastructure/hooks/useAuthentication";
import { UserRole } from "../../../../../common/enums";
import { useEffect, useMemo, useState } from "react";
import { UserRoleLabels } from "../../../../../common/labels";
import { IWorkingHoursData } from "../../../../../common/components/common/workingHoursDialog/models/models";
import {
  studioHidesCommissions,
  studioHidesPlans,
} from "../../../../../common/infrastructure/helpers/studioHelper";

export const useEmployeeWizardPage = (pageMode: PageMode) => {
  const { selectedStudioId, showServicesTypeOptions, userStudios } =
    useAppContext();
  const navigate = useNavigate();
  const { currentUser, currentUserRole } = useAuthentication();
  const [employeeRoles, setEmployeeRoles] = useState(getEmployeeRoles);
  const [disableRoleDropdown, setDisableRoleDropdown] = useState(false);
  const [isStudioSelectionDisabled, setIsStudioSelectionDisabled] =
    useState(false);

  const { mutate } = useApiMutation();

  const { id } = useParams();

  const { data: result, isLoading } = useApiQuery<IEmployeeWizardFormModel>({
    queryKey: [endpointsMetadata.employee.wizardGetEmployee, id],
    endpoint: `${endpointsMetadata.employee.wizardGetEmployee}?employeeId=${id}`,
    enabled: id !== undefined,
  });

  const { data: workingHoursData } = useApiQuery<IWorkingHoursData>({
    queryKey: [endpointsMetadata.workingHours.getUserWorkingHours, id],
    endpoint: `${endpointsMetadata.workingHours.getUserWorkingHours}/${id}`,
    enabled: id !== undefined,
  });

  const form = useEmployeeWizardForm(result);

  // Only the employee themselves or a higher role may change the email and phone
  // (a password reset goes there), so e.g. a manager edits the owner's other
  // details with these two locked. The backend refuses the change too.
  const areSignInDetailsLocked =
    !!result &&
    result.id !== currentUser?.id &&
    !outranks(currentUserRole, result.role);

  // Changing the role also needs a higher role than the employee's, so e.g. a
  // manager can't demote another manager (or themselves).
  const isRoleLocked = !!result && !outranks(currentUserRole, result.role);
  const { showLockOthersEventsToggle } = useLockOthersEventsToggle(form);

  const availableStudios = useMemo(
    () =>
      userStudios.map((s) => ({
        id: s.id,
        name: s.name,
        types: s.types ?? [],
        supportedPaymentTypes: s.supportedPaymentTypes ?? [],
        // A studio that hides its plans has no plan commissions to set.
        usesPlans: !studioHidesPlans(s),
        // A studio that does not use commissions has no percentages to set at all.
        usesCommissions: !studioHidesCommissions(s),
      })),
    [userStudios],
  );

  const pageTitle = `${
    pageMode === "add" ? "Добавяне" : "Редактиране"
  } на служител`;
  const actionButtonLabel = pageMode === "add" ? "Добавяне" : "Запазване";

  const handleSubmit: SubmitHandler<IEmployeeWizardFormModel> = async (
    data,
  ) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber);
    }

    mutate(
      {
        endpoint: endpointsMetadata.employee.wizard,
        method: "post",
        invalidateKeys: [
          [endpointsMetadata.employee.getAll],
          [endpointsMetadata.employee.wizardGetEmployee],
        ],
        globalIsLoading: true,
        payload: { ...data },
      },
      {
        onSuccess: () => {
          navigate(routePaths.employees);
        },
      },
    );
  };

  // Handle role-specific logic (Tenant profiles)
  useEffect(() => {
    if (!!result) {
      if (result.role === UserRole.Tenant) {
        setDisableRoleDropdown(true);
        setIsStudioSelectionDisabled(true);
        const ownerRole = {
          id: UserRole.Tenant,
          name: UserRoleLabels[UserRole.Tenant],
        };
        setEmployeeRoles([ownerRole, ...employeeRoles]);
      }
    }
  }, [result, isLoading]);

  // For create mode: set default studio to currently selected studio
  useEffect(() => {
    if (pageMode === "add" && selectedStudioId && userStudios.length > 0) {
      const selectedStudio = userStudios.find(
        (s) => s.id === selectedStudioId,
      );
      if (selectedStudio) {
        const defaultStudio: IEmployeeStudioFormModel = {
          studioId: selectedStudio.id,
          studioName: selectedStudio.name,
          commissionPercent: null,
          weekendCommissionPercent: null,
          individualCommissionPercent: null,
          individualWeekendCommissionPercent: null,
          multisportCommissionPercent: null,
          multisportWeekendCommissionPercent: null,
          coolFitCommissionPercent: null,
          coolFitWeekendCommissionPercent: null,
          serviceCommissionPercent: null,
          serviceWeekendCommissionPercent: null,
          planCommissionPercent: null,
          planWeekendCommissionPercent: null,
        };
        form.setValue("studios", [defaultStudio]);
      }
    }
  }, [pageMode, selectedStudioId, userStudios]);

  return {
    id,
    form,
    pageTitle,
    isLoading,
    actionButtonLabel,
    employeeRoles,
    disableRoleDropdown: disableRoleDropdown || isRoleLocked,
    isStudioSelectionDisabled,
    areSignInDetailsLocked,
    availableStudios,
    workingHoursData,
    showServicesTypeOptions,
    showLockOthersEventsToggle,
    handleSubmit,
  };
};
